import { useEffect, useRef, useState } from "react";
import { app } from "../../../lib/firebase/config";
import {
  DocumentData,
  DocumentReference,
  doc,
  getDoc,
  getFirestore,
} from "firebase/firestore";
import { UserTypes } from "../../../types/auth";
import { StyledImpl2FA } from "./StyledImpl2FA";
import Button from "../../vetrina/components/Button";
import { startSignInWith2FA, endSignInWith2FA } from "../../../utils/auth/2FA";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { setIsLoading, setMultifactorError } from "../../../store/slices/main";
import { RootState } from "../../../store/store";
import { reportError } from "../../../lib/errorReporting";

const verifyTitle = "Verifica il tuo codice";
const verifyDescription =
  "Per favore, inserisci il codice che ti abbiamo inviato via SMS.";

const Impl2FA = () => {
  const db = getFirestore(app);

  const [errorMessage, setErrorMessage] = useState<string>("");
  const [verificationCode, setVerificationCode] = useState<string>("");
  const [verificationId, setVerificationId] = useState<any | null>(null);
  const [allowMfaUi, setAllowMfaUi] = useState(false);
  const [userType, setUserType] = useState<UserTypes | null>(null);
  const smsStartRef = useRef(false);

  const urlParams = new URLSearchParams(window.location.search);
  const cf = urlParams.get("cf");
  const isVerifing = urlParams.get("code");
  const from = urlParams.get("from");

  const multiFactorError = useSelector(
    (state: RootState) => state.main.multifactorError
  );

  const externalUserRef = cf && doc(db, "external_users", cf);
  const internalUserRef = cf && doc(db, "internal_users", cf);

  const navigate = useNavigate();
  const dr = useDispatch<any>();

  useEffect(() => {
    const fetchUser = async () => {
      if (!isVerifing) {
        if (cf) {
          const externalUser = await getUser(externalUserRef);
          const internalUser = await getUser(internalUserRef);
          if (externalUser?.exists()) {
            navigate("/sign-in", { replace: true });
            return;
          }
          if (internalUser?.exists()) {
            navigate("/sign-in-admin", { replace: true });
            return;
          }
        } else {
          navigate("/sign-in-admin", { replace: true });
          return;
        }
        navigate("/sign-in", { replace: true });
        return;
      }

      const externalUser = await getUser(externalUserRef);
      const internalUser = await getUser(internalUserRef);

      if (externalUser?.exists()) {
        setUserType(externalUser.data().user_type);
      } else if (internalUser?.exists()) {
        setUserType(internalUser.data().admin ? UserTypes.ADMIN : null);
      }

      setAllowMfaUi(true);
    };

    void fetchUser().catch((error) =>
      reportError(error, { operation: "auth.mfa.userLookup" })
    );
  }, [cf, isVerifing, navigate, externalUserRef, internalUserRef]);

  useEffect(() => {
    if (!isVerifing || !allowMfaUi || smsStartRef.current) return;
    if (!multiFactorError) return;
    smsStartRef.current = true;
    (async () => {
      const { error: resError, verificationId: vid } =
        await startSignInWith2FA({
          error: multiFactorError,
          buttonId: "start-2fa-btn",
        });

      setVerificationId(vid);
      dr(setMultifactorError(resError));
    })();
  }, [allowMfaUi, dr, isVerifing, multiFactorError]);

  useEffect(() => {
    if (!isVerifing || !allowMfaUi) return;
    if (!multiFactorError && isVerifing) {
      switch (userType) {
        case UserTypes.ADMIN:
          navigate("/sign-in-admin");
          break;
        case UserTypes.DATORE:
          navigate("/sign-in");
          break;
      }
    }
  }, [allowMfaUi, isVerifing, multiFactorError, navigate, userType]);

  const verifyCode = async () => {
    try {
      if (!verificationCode || verificationCode.length < 6) {
        setErrorMessage("Inserisci il codice di verifica");
        return;
      }

      if (!from || !multiFactorError) {
        setErrorMessage("Si è verificato un errore, riefettua il login");
        return;
      }

      const res = await endSignInWith2FA({
        error: multiFactorError,
        verificationId: verificationId,
        verificationCode: verificationCode,
      });

      if (res?.user) {
        navigate(decodeURIComponent(from));
      } else {
        setErrorMessage("Si è verificato un errore, riefettua il login");
      }
    } catch (e: any) {
      await reportError(e, { operation: "auth.mfa.code.verify" });
      if (e.message.includes("(auth/invalid-verification-code)")) {
        setErrorMessage("Codice di verifica errato");
        return;
      }
      setErrorMessage("Si è verificato un errore");
      console.log(e);
    }
  };

  const handleBtn = async () => {
    if (!isVerifing) return;
    dr(setIsLoading(true));
    await verifyCode();
    dr(setIsLoading(false));
  };

  if (!allowMfaUi) {
    return null;
  }

  return (
    <StyledImpl2FA>
      <div className="main-wrapper">
        <h3 className="dialog-title">{verifyTitle}</h3>
        <p className="dialog-description">{verifyDescription}</p>
        <div id="recaptcha-container"></div>
        {isVerifing ? (
          <input
            type="number"
            onChange={(e) => {
              setVerificationCode(e.target.value);
            }}
          />
        ) : null}
        {errorMessage && <p className="error-message">{errorMessage}</p>}
        <Button id="start-2fa-btn" onClick={handleBtn} className="main-btn">
          Verifica
        </Button>
      </div>
    </StyledImpl2FA>
  );
};

const getUser: any = async (
  userRef: "" | DocumentReference<DocumentData> | null
) => {
  if (!userRef) return null;
  return await getDoc(userRef);
};

export default Impl2FA;
